import { getBirthdayFromSsn } from 'common/src/vtj/ssn'
import { format } from 'date-fns'
import { observer } from 'mobx-react'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Block, Heading } from 'suomifi-ui-components'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { Columns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import AetSummaryContactInfo from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetOrderSummary/AetSummaryContactInfo'
import AetSummaryPriceInfo from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetOrderSummary/AetSummaryPriceInfo'
import AetSummaryDeliveryMethod from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetOrderSummary/AetSummaryDeliveryMethod'
import AetSummaryOrdererInfo from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetOrderSummary/AetSummaryOrdererInfo'
import AetSummaryTodistusInfo from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetOrderSummary/AetSummaryTodistusInfo.react'
import InfoItem from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/InfoItem.react'

import { FormContainer } from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useAetSignerForm } from 'todistukset-ui/src/vtj/stores/aet-signer-form.store'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'

const AetSummaryStep: StepComponent = observer(
  forwardRef(function AetSummaryStep({ titleId }, _ref) {
    const form = useAetSignerForm()
    if (!form.aetTodistuspyynto) {
      return <></>
    }
    const {
      aetTodistuspyynto: {
        orderer: { lastName: spouseALastName, firstNames: spouseAFirstNames },
        aetLisaosa: {
          spouseBKeepingCurrentLastName,
          spouseBNewLastName,
          isKirkollinenVihkiminen,
          placeOfMarriage,
        },
        todistusLanguage,
      },
      orderer,
      contactInfo,
    } = form

    const ordererFullName = `${finalEditableFieldValue(
      spouseAFirstNames
    )} ${finalEditableFieldValue(spouseALastName)}`

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetSignerSummaryMainTitle" />
          </Heading>
          <LocalizedParagraphs
            stringKey="aetSignerSummaryMainDescription_paragraphs"
            values={{ ordererFullName }}
          />
        </Block>
        <AetSummaryOrdererInfo
          orderer={orderer}
          keepingCurrentLastName={spouseBKeepingCurrentLastName}
          newLastName={spouseBNewLastName}
        />
        <AetSummarySpouseInfo />
        <AetSummaryTodistusInfo
          isKirkollinenVihkiminen={isKirkollinenVihkiminen}
          todistusLanguage={todistusLanguage}
          placeOfMarriage={placeOfMarriage}
        />
        <AetSummaryDeliveryMethod />
        <AetSummaryContactInfo contactInfo={contactInfo} />
        <AetSummaryPriceInfo />
      </FormContainer>
    )
  })
)

const AetSummarySpouseInfo: React.FC = observer(() => {
  const { t } = useTranslation()
  const form = useAetSignerForm()
  if (!form.aetTodistuspyynto) {
    return <></>
  }
  const {
    aetTodistuspyynto: {
      orderer: {
        firstNames: spouseAFirstNames,
        lastName: spouseALastName,
        hetu: spouseAHetu,
      },
      aetLisaosa: { spouseAKeepingCurrentLastName, spouseANewLastName },
    },
  } = form

  const spousesName = `${finalEditableFieldValue(
    spouseALastName
  )}, ${finalEditableFieldValue(spouseAFirstNames)}`

  const spousesBirthdate = format(getBirthdayFromSsn(spouseAHetu), 'dd.MM.yyyy')

  const spousesNewLastName = finalEditableFieldValue(
    spouseAKeepingCurrentLastName
  )
    ? finalEditableFieldValue(spouseALastName) || ''
    : finalEditableFieldValue(spouseANewLastName) || ''

  return (
    <Block variant="section">
      <Heading variant="h3">
        <LocalizedString stringKey="aetSummarySpouseInfoTitle" />
      </Heading>
      <InfoItem title={t('aetSummaryNameLabel')} content={spousesName} />
      <Columns>
        <InfoItem
          title={t('aetSummarySpouseBirthdateLabel')}
          content={spousesBirthdate}
        />
        <InfoItem
          title={t('aetSummaryLastNameAfterMarriageLabel')}
          content={spousesNewLastName}
        />
      </Columns>
    </Block>
  )
})

export default AetSummaryStep
